import {
  ChartUserOverrides,
  ChartUserOverridesChartTypeEnum,
  WebDashboardContentSpecBlocksInner,
} from "@coeff/agent-types";
import { AnalyticsEventNameType } from "@coeff/api";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";

import {
  ChartTypeAreaIcon,
  ChartTypeBarIcon,
  ChartTypeDoughnutIcon,
  ChartTypeFunnelIcon,
  ChartTypeHorizontalBarIcon,
  ChartTypeLineIcon,
  ChartTypePieIcon,
  CloseIcon,
  Sparkle2,
} from "../../components/Icons";
import { useTrack } from "../../utils";

import {
  BUTTON_CLASS,
  CLOSE_BUTTON_CLASS,
  EditIconChoiceRow,
  EditRow,
  EditSelectRow,
  EditTextArea,
  EditTextField,
  EditToggleRow,
  IconChoice,
} from "./BlockEditControls";
import { getChartCapabilities, resolveCapabilityType } from "./chartCapabilities";
import {
  ChartEditSettings,
  PER_SEGMENT_CHART_TYPES,
  readLiveChartSettings,
  rendersWithCreateChart,
} from "./chartUserOverrides";
import { CARD_SURFACE_STYLE, DashboardWindow } from "./dashboardHelpers";
import { MetricIconPicker } from "./MetricIconPicker";
import { useBlockEditCommit } from "./useBlockEditCommit";
import { useDismissOnOutside } from "./useDismissOnOutside";
import { usePopoverPosition } from "./usePopoverPosition";
import { useWebDashboardViewerContext } from "./WebDashboardViewerContext";

export interface BlockEditUpdates {
  title?: string;
  description?: string;
  markdown?: string;
  chartType?: string;
  aiEditPrompt?: string;
  /**
   * With aiEditPrompt: draft the prompt into the chat composer instead of auto-submitting,
   * so the user reads what the AI will be asked and sends it themselves (the AI-convert
   * targets use this — the conversion rewrites the block, so it deserves a look first).
   */
  aiEditDraft?: boolean;
  openChat?: boolean;
  // Metric-card-only fields (see MetricCardBlock in @coeff/agent-types). Icon color is
  // deliberately not editable here — it is owned by the theme.
  icon?: string;
  showIcon?: boolean;
  showComparison?: boolean;
  // Chart-only: persisted display overrides (see ChartUserOverrides in @coeff/agent-types).
  chartOverrides?: ChartUserOverrides;
}

interface Props {
  block: WebDashboardContentSpecBlocksInner;
  /** The AI/chat routes (Edit in chat, AI image prompt, AI convert). Closes the popover. */
  onSave: (blockId: string, updates: BlockEditUpdates) => void;
  /**
   * Cache-only write: the block updates on the spot, nothing is saved yet. Used by the click
   * controls on every change and by Done for the text fields.
   */
  onApplyLocal: (blockId: string, updates: BlockEditUpdates) => void;
  /** Ends the session: one save for everything applied locally since the popover opened. */
  onCommit: (blockId: string) => void;
  onClose: () => void;
  targetWindow: DashboardWindow;
}

function getBlockEditTitle(blockType: string): string {
  switch (blockType) {
    case "chart":
      return "Edit Chart";
    case "data_table":
      return "Edit Table";
    case "section_header":
      return "Edit Section Header";
    case "text_block":
      return "Edit Text Block";
    case "ai_image":
      return "Edit AI Image";
    case "metric_card":
      return "Edit KPI";
    default:
      return "Edit Block";
  }
}

function getInitialTitle(block: WebDashboardContentSpecBlocksInner): string {
  if (block.type === "metric_card") {
    return block.label;
  }
  if (block.type === "text_block" || block.type === "ai_image") {
    return "";
  }
  return (block as any).title ?? "";
}

function getInitialDescription(block: WebDashboardContentSpecBlocksInner): string {
  if (block.type === "section_header") {
    return block.description ?? "";
  }
  if (block.type === "chart" || block.type === "data_table") {
    return (block as any).description ?? "";
  }
  return "";
}

// Figma edit popover frames; height is content-driven in both. The chart variant
// (node 6818:30107) is wider than the KPI one (node 6837:31940) to fit the type switcher.
const POPOVER_WIDTH_DEFAULT = 280;
const POPOVER_WIDTH_CHART = 320;

/**
 * The designed switcher artwork per switchable type (see components/Icons). Separate from
 * CHART_TYPE_META below, which serves the AI-convert dropdown — that is a text menu with small
 * inline glyphs, and covers types the switcher never shows.
 */
const CHART_TYPE_SWITCHER_ICONS: Record<
  ChartUserOverridesChartTypeEnum,
  React.FC<React.SVGProps<Element>>
> = {
  bar: ChartTypeBarIcon,
  horizontalBar: ChartTypeHorizontalBarIcon,
  line: ChartTypeLineIcon,
  area: ChartTypeAreaIcon,
  pie: ChartTypePieIcon,
  doughnut: ChartTypeDoughnutIcon,
  funnel: ChartTypeFunnelIcon,
};

/**
 * Label + Material icon per spec chart_type, for the AI-convert dropdown shown on charts whose
 * data shape can't be switched mechanically (see ChartUserOverridesChartTypeEnum). horizontalBar reuses
 * bar_chart rotated 90°.
 */
const CHART_TYPE_META: Record<string, { label: string; icon: string; rotate?: boolean }> = {
  bar: { label: "Bar", icon: "bar_chart" },
  horizontalBar: { label: "Horizontal bar", icon: "bar_chart", rotate: true },
  line: { label: "Line", icon: "show_chart" },
  area: { label: "Area", icon: "area_chart" },
  pie: { label: "Pie", icon: "pie_chart" },
  doughnut: { label: "Doughnut", icon: "donut_large" },
  funnel: { label: "Funnel", icon: "filter_alt" },
  scatter: { label: "Scatter", icon: "scatter_plot" },
  bubble: { label: "Bubble", icon: "bubble_chart" },
  heatmap: { label: "Heatmap", icon: "grid_on" },
  treemap: { label: "Treemap", icon: "dashboard" },
  sankey: { label: "Sankey", icon: "account_tree" },
  waterfall: { label: "Waterfall", icon: "waterfall_chart" },
  bridge: { label: "Bridge", icon: "candlestick_chart" },
  gantt: { label: "Gantt", icon: "view_timeline" },
};

/**
 * How the non-obvious conversions should reinterpret the data — appended to the AI edit
 * prompt so the agent lands on the semantics the table row promises.
 */
const AI_CONVERT_HINTS: Record<string, string> = {
  sankey: "aggregating the flows into totals per node",
  gantt: "charting each task's duration (end minus start)",
  heatmap: "aggregating away one of the grid dimensions",
  treemap: "flattening the top-level categories into label/value pairs",
  scatter: "aggregating or binning the points as needed",
  bubble: "aggregating or binning the points as needed",
  waterfall: "converting the floating [start, end] steps appropriately",
  bridge: "converting the floating [start, end] steps appropriately",
};

/** Sentinel option: opens the chat (block chip inserted, nothing auto-submitted). */
const OTHER_CONVERT_TARGET = "__other__";

const AI_CONVERT_HINT_TEXT =
  "This chart's data shape can't be switched directly. Picking a target drafts an AI request " +
  "in chat — review it and send. The sparkle opens chat to describe your own.";

export const BlockEditPopover: React.FC<Props> = ({
  block,
  onSave,
  onApplyLocal,
  onCommit,
  onClose,
  targetWindow,
}) => {
  const track = useTrack();
  const { dashboardId, dashboardName, spreadsheetId } = useWebDashboardViewerContext();
  const popoverRef = useRef<HTMLDivElement>(null);
  const blockType = block.type;
  const { style: positionStyle } = usePopoverPosition(
    popoverRef,
    true,
    targetWindow,
    blockType === "chart" ? POPOVER_WIDTH_CHART : POPOVER_WIDTH_DEFAULT
  );
  const isAiImage = blockType === "ai_image";
  const isMetricCard = block.type === "metric_card";
  /**
   * Chart blocks get the manual controls only when their render JS goes through
   * createChart — the one path that applies chart_overrides. A chart block drawing custom
   * HTML/SVG still gets title/description and Edit in chat, but no controls that would
   * silently persist overrides nothing ever reads.
   */
  const isChart = block.type === "chart" && rendersWithCreateChart(block.render_function_js);
  // custom_html qualifies: its spec has a title (rendered by the React card header, not the
  // block's own markup) but no description, and no other manual controls apply to it.
  const hasTitle =
    blockType === "chart" ||
    blockType === "data_table" ||
    blockType === "section_header" ||
    blockType === "metric_card" ||
    blockType === "custom_html";
  const hasDescription =
    blockType === "chart" || blockType === "data_table" || blockType === "section_header";

  const [aiPrompt, setAiPrompt] = useState("");
  const aiTextareaRef = useRef<HTMLTextAreaElement>(null);

  const { draft, setDraftField, isDirty, saveDraft, applyLocal } = useBlockEditCommit<
    { title: string; description: string },
    BlockEditUpdates
  >({
    blockId: block.block_id,
    blockType: block.type,
    initialDraft: { title: getInitialTitle(block), description: getInitialDescription(block) },
    onApplyLocal: updates => onApplyLocal(block.block_id, updates),
    onDraftCommit: text => {
      const updates: BlockEditUpdates = {};
      if (hasTitle) {
        updates.title = text.title;
      }
      if (hasDescription) {
        updates.description = text.description;
      }
      // Text rides along in the session's single save rather than getting its own PUT.
      onApplyLocal(block.block_id, updates);
    },
  });

  // Done: fold the text draft into the session, then end it. onClose commits too (dismissal
  // saves — see DraggableBlock), and commit is idempotent, so calling both is harmless.
  const handleDone = useCallback(() => {
    if (isDirty) {
      saveDraft();
    }
    onCommit(block.block_id);
    onClose();
  }, [isDirty, saveDraft, onCommit, onClose, block.block_id]);

  // Unmount without onClose (parent re-keyed, editing disabled mid-session) still saves.
  // Idempotent with the onClose commit, so the common path costs nothing extra.
  const onCommitRef = useRef(onCommit);
  onCommitRef.current = onCommit;
  useEffect(() => {
    const blockId = block.block_id;
    return () => {
      onCommitRef.current(blockId);
    };
  }, [block.block_id]);

  useDismissOnOutside(popoverRef, onClose, targetWindow);

  useEffect(() => {
    if (isAiImage && aiTextareaRef.current) {
      aiTextareaRef.current.focus();
    }
  }, [isAiImage]);

  /**
   * What the chart's controls fall back to for keys the user hasn't overridden: the LIVE
   * Chart.js instance's raw config, so they reflect what the render JS actually drew rather
   * than blank defaults. Captured once at mount — after that, an overridden key is answered
   * from the spec, so the baseline only ever supplies untouched keys.
   */
  const [chartBaseline] = useState<ChartEditSettings | null>(() => {
    if (block.type !== "chart" || !isChart) {
      return null;
    }
    const live = readLiveChartSettings(targetWindow, `block_${block.block_id}`);
    return {
      effectiveType: live?.effectiveType ?? block.chart_type,
      stacking: live?.stacking ?? "none",
      legendPosition: live?.legendPosition ?? "top",
      dataLabels: live?.dataLabels ?? "none",
      showXAxis: live?.showXAxis ?? true,
      showYAxis: live?.showYAxis ?? true,
      // Unknown (chart not mounted) counts as multi-series so the control stays available.
      seriesCount: live?.seriesCount ?? 2,
      valueAxisCount: live?.valueAxisCount ?? 1,
      hasOwnDatalabels: live?.hasOwnDatalabels ?? true,
    };
  });

  /**
   * Current control values, derived from the SPEC layered over that baseline — never from
   * local state. The local apply's cache write is synchronous, so this updates in the same
   * tick as the click; and because a failed session save rolls the cache back to the
   * pre-session snapshot, a rejected session snaps the controls back instead of desyncing.
   */
  const chartSettings = useMemo<ChartEditSettings | null>(() => {
    if (block.type !== "chart" || !chartBaseline) {
      return null;
    }
    const overrides = block.chart_overrides;
    return {
      effectiveType: overrides?.chart_type ?? chartBaseline.effectiveType,
      stacking: overrides?.stacking ?? chartBaseline.stacking,
      legendPosition: overrides?.legend_position ?? chartBaseline.legendPosition,
      dataLabels: overrides?.data_labels ?? chartBaseline.dataLabels,
      showXAxis: overrides?.show_x_axis ?? chartBaseline.showXAxis,
      showYAxis: overrides?.show_y_axis ?? chartBaseline.showYAxis,
      seriesCount: chartBaseline.seriesCount,
      valueAxisCount: chartBaseline.valueAxisCount,
      hasOwnDatalabels: chartBaseline.hasOwnDatalabels,
    };
  }, [block, chartBaseline]);

  /**
   * Which controls this chart gets — one table per chart type (chartCapabilities.ts), refined
   * by what the live chart showed (series count, value-axis count, own label config).
   */
  // Structural types (waterfall, heatmap, ...) render as plain Chart.js types, so the spec's
  // chart_type decides the row for them; see resolveCapabilityType.
  const capabilityType = useMemo(
    () =>
      chartSettings
        ? resolveCapabilityType(
            block.type === "chart" ? block.chart_type : undefined,
            chartSettings.effectiveType
          )
        : null,
    [block, chartSettings]
  );
  const capabilities = useMemo(
    () =>
      chartSettings && capabilityType
        ? getChartCapabilities(capabilityType, {
            seriesCount: chartSettings.seriesCount,
            valueAxisCount: chartSettings.valueAxisCount,
            hasOwnDatalabels: chartSettings.hasOwnDatalabels,
          })
        : null,
    [chartSettings, capabilityType]
  );

  /**
   * Applies one chart override locally. `chart_overrides` is a single spec field, so the
   * whole object is written, merged over what the spec currently holds — untouched aspects
   * stay owned by the render JS. Reading the merge base from `block` (not a mount-time
   * snapshot) is what makes a burst of clicks compose instead of clobbering each other.
   */
  const applyChartOverride = useCallback(
    (patch: ChartUserOverrides) => {
      const existing = block.type === "chart" ? block.chart_overrides ?? {} : {};
      const updates: BlockEditUpdates = { chartOverrides: { ...existing, ...patch } };
      if (patch.chart_type != null) {
        // Keep the spec's own chart_type in sync so the agent knows the real type.
        updates.chartType = patch.chart_type;
      }
      applyLocal(updates);
    },
    [block, applyLocal]
  );

  const handleAiSubmit = useCallback(() => {
    if (!aiPrompt.trim()) {
      return;
    }
    track(AnalyticsEventNameType.VibeDashBlockEdited, {
      vibe_dash_id: dashboardId,
      ss_id: spreadsheetId,
      vibe_dash_name: dashboardName,
      vibe_dash_block_id: block.block_id,
      vibe_dash_block_type: block.type,
      vibe_dash_edit_method: "ai",
    });
    onSave(block.block_id, { aiEditPrompt: aiPrompt.trim() });
    onClose();
  }, [
    block.block_id,
    block.type,
    aiPrompt,
    onSave,
    onClose,
    track,
    dashboardId,
    spreadsheetId,
    dashboardName,
  ]);

  const handleEditInChat = useCallback(() => {
    track(AnalyticsEventNameType.VibeDashBlockEdited, {
      vibe_dash_id: dashboardId,
      ss_id: spreadsheetId,
      vibe_dash_name: dashboardName,
      vibe_dash_block_id: block.block_id,
      vibe_dash_block_type: block.type,
      vibe_dash_edit_method: "ai",
    });
    onSave(block.block_id, { openChat: true });
    onClose();
  }, [
    block.block_id,
    block.type,
    onSave,
    onClose,
    track,
    dashboardId,
    spreadsheetId,
    dashboardName,
  ]);

  // For charts whose data shape can't be converted mechanically (scatter, heatmap,
  // waterfall, ...): picking a curated target routes to the AI edit path, which rebuilds
  // the SQL/data shape. Drafted into the chat composer rather than auto-submitted — the
  // conversion rewrites the block, so the user reads the request and sends it themselves.
  // The per-source hint steers the agent to the reinterpretation the target implies.
  const handleAiConvertType = useCallback(
    (targetType: string) => {
      track(AnalyticsEventNameType.VibeDashBlockEdited, {
        vibe_dash_id: dashboardId,
        ss_id: spreadsheetId,
        vibe_dash_name: dashboardName,
        vibe_dash_block_id: block.block_id,
        vibe_dash_block_type: block.type,
        vibe_dash_edit_method: "ai",
      });
      const hint = block.type === "chart" ? AI_CONVERT_HINTS[block.chart_type] : undefined;
      onSave(block.block_id, {
        aiEditPrompt: `Convert this chart to a ${
          CHART_TYPE_META[targetType]?.label ?? targetType
        } chart (chart_type "${targetType}")${
          hint ? `, ${hint}` : ""
        }, rebuilding the query and data shape as needed.`,
        aiEditDraft: true,
      });
      onClose();
    },
    [block, onSave, onClose, track, dashboardId, spreadsheetId, dashboardName]
  );

  /**
   * Icon row for the non-switchable types, mirroring the switcher's look: the source type
   * (selected, a no-op) plus its curated AI-convert targets, and a sparkle for "describe
   * your own". Only the seven switchable types have designed icons, so these render the
   * Material glyphs from CHART_TYPE_META.
   */
  const aiConvertChoices = useMemo<IconChoice[]>(() => {
    if (!chartSettings || !capabilities || capabilities.typeSwitch || !capabilityType) {
      return [];
    }
    const source = capabilityType;
    const sourceMeta = CHART_TYPE_META[source];
    return [
      {
        value: source,
        materialIcon: sourceMeta?.icon ?? "insert_chart",
        materialRotate: sourceMeta?.rotate,
        title: `${sourceMeta?.label ?? source} (current)`,
      },
      ...capabilities.aiConvertTargets.map(target => ({
        value: target,
        materialIcon: CHART_TYPE_META[target].icon,
        materialRotate: CHART_TYPE_META[target].rotate,
        title: `Convert to ${CHART_TYPE_META[target].label.toLowerCase()} with AI`,
      })),
      {
        value: OTHER_CONVERT_TARGET,
        Icon: Sparkle2,
        title: "Other — describe the conversion in chat",
      },
    ];
  }, [chartSettings, capabilities, capabilityType]);

  const chartTypeChoices = useMemo<IconChoice<ChartUserOverridesChartTypeEnum>[]>(() => {
    if (!chartSettings) {
      return [];
    }
    // Which side carries the categories today; undefined for the segment types, which have no
    // cartesian orientation.
    const indexSideOf = (type: string): "x" | "y" | undefined =>
      PER_SEGMENT_CHART_TYPES.has(type) ? undefined : type === "horizontalBar" ? "y" : "x";
    const currentSide = indexSideOf(chartSettings.effectiveType);

    return Object.values(ChartUserOverridesChartTypeEnum).map(type => {
      const isSelected = chartSettings.effectiveType === type;
      // Per-segment targets (pie/doughnut/funnel) color one slice per label; 2+ datasets
      // would render as concentric rings or collapse, so those are disabled for
      // multi-series charts.
      const segmentBlocked =
        !isSelected && PER_SEGMENT_CHART_TYPES.has(type) && chartSettings.seriesCount >= 2;
      // An orientation flip swaps the category and value sides, and the block's own axis
      // titles/tick formatting must move with them. With two value axes there is no partner
      // for the second one to swap with, so the flip has no faithful result — offer Edit in
      // chat instead of silently mislabelling both axes.
      const targetSide = indexSideOf(type);
      const orientationBlocked =
        !isSelected &&
        currentSide !== undefined &&
        targetSide !== undefined &&
        targetSide !== currentSide &&
        chartSettings.valueAxisCount > 1;
      const disabled = segmentBlocked || orientationBlocked;
      const typeLabel = type === "horizontalBar" ? "horizontal bar" : type.toLowerCase();
      return {
        value: type,
        Icon: CHART_TYPE_SWITCHER_ICONS[type],
        title: orientationBlocked
          ? `Dual-axis charts can't switch to ${typeLabel} — use Edit in chat`
          : disabled
          ? `Multi-series charts can't convert to ${typeLabel} — use Edit in chat`
          : // Named the way the Figma labels the tooltip ("Pie chart"), not the bare spec
            // value — the lowercase form stays in the sentences above, where it reads.
            `${CHART_TYPE_META[type].label} chart`,
        disabled,
      };
    });
  }, [chartSettings]);

  return (
    <div
      ref={popoverRef}
      data-testid="block-edit-popover"
      className="absolute z-50 text-card-foreground rounded-[12px] shadow-2xl border border-card-border block-fade-in pt-[12px]"
      style={{ ...positionStyle, ...CARD_SURFACE_STYLE }}
      onClick={e => e.stopPropagation()}
      onMouseDown={e => e.stopPropagation()}
    >
      {/* Header — 24px row at the popover's 12px gutter, per the Figma edit popover. */}
      <div className="flex items-center justify-between gap-[6px] h-[24px] px-[12px]">
        <span className="text-[14px] leading-[18px] font-bold">{getBlockEditTitle(blockType)}</span>
        <button onClick={onClose} aria-label="Close" className={CLOSE_BUTTON_CLASS}>
          <CloseIcon width={10} height={10} />
        </button>
      </div>

      {isAiImage ? (
        <div className="px-[12px] pt-[8px] pb-[12px] space-y-[12px]">
          <div className="text-xs text-card-muted-foreground">
            Describe the changes you want to make to this image.
          </div>
          <div className="relative">
            <textarea
              ref={aiTextareaRef}
              value={aiPrompt}
              onChange={e => setAiPrompt(e.target.value)}
              onKeyDown={e => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  handleAiSubmit();
                }
              }}
              placeholder="e.g. Change the style, update colors..."
              className="w-full border border-card-foreground/20 rounded-md px-3 py-2.5 pr-10 text-sm text-card-foreground caret-primary-button-background focus:outline-none focus:ring-2 focus:ring-primary-button-background focus:border-transparent resize-none bg-card-foreground/5 placeholder:text-card-muted-foreground"
              rows={4}
            />
            <button
              onClick={handleAiSubmit}
              disabled={!aiPrompt.trim()}
              className="absolute bottom-2.5 right-2.5 w-7 h-7 rounded-full bg-primary-button-background text-primary-button-foreground flex items-center justify-center hover:bg-primary-button-hover-background disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer transition-colors"
            >
              <span className="material-icons !text-base">arrow_upward</span>
            </button>
          </div>
        </div>
      ) : (
        <div className="px-[12px] pt-[8px] pb-0 space-y-[8px]">
          {/* Text fields are held as a draft and folded into the session on Done. */}
          {hasTitle && (
            <EditTextField
              label="Title"
              value={draft.title}
              onChange={value => setDraftField("title", value)}
              placeholder={`Enter ${blockType === "metric_card" ? "label" : "title"}...`}
            />
          )}

          {hasDescription && (
            <EditTextArea
              label="Description"
              value={draft.description}
              onChange={value => setDraftField("description", value)}
              placeholder="Enter description"
            />
          )}

          {/* Everything below applies on click and is saved once when the popover closes.
              Per the Figma, controls are 32px rows separated by the container's 8px gap —
              there are no rules between them. */}
          {isMetricCard && block.type === "metric_card" && (
            <>
              <EditRow label="Icon">
                <MetricIconPicker
                  icon={block.icon}
                  onChange={icon => applyLocal({ icon })}
                  // `show_icon` / `show_comparison` are optional spec fields where unset
                  // means shown, so only an explicit false reads as off.
                  showIcon={block.show_icon !== false}
                  onShowIconChange={showIcon => applyLocal({ showIcon })}
                  targetWindow={targetWindow}
                />
              </EditRow>
              <EditToggleRow
                label="Show comparison"
                checked={block.show_comparison !== false}
                onChange={showComparison => applyLocal({ showComparison })}
              />
            </>
          )}

          {isChart && chartSettings && capabilities && (
            <>
              {/* Type switching is limited to the family sharing the {labels, datasets[]}
                  data shape. Structurally different charts (scatter, heatmap, waterfall,
                  ...) get the same icon-row treatment, but a click drafts an AI-convert
                  request into the chat for the user to review and send — the conversion
                  rebuilds the data shape, so it is not applied silently. */}
              {capabilities.typeSwitch ? (
                <EditIconChoiceRow
                  label="Chart type"
                  value={chartSettings.effectiveType}
                  choices={chartTypeChoices}
                  onChange={type => applyChartOverride({ chart_type: type })}
                />
              ) : (
                <EditIconChoiceRow
                  label="Chart type"
                  hint={AI_CONVERT_HINT_TEXT}
                  value={capabilityType ?? chartSettings.effectiveType}
                  choices={aiConvertChoices}
                  onChange={value => {
                    if (value === OTHER_CONVERT_TARGET) {
                      handleEditInChat();
                    } else if (value !== capabilityType) {
                      handleAiConvertType(value);
                    }
                  }}
                />
              )}

              {capabilities.stacking !== "hidden" && (
                <EditSelectRow
                  label="Stacking"
                  value={chartSettings.stacking}
                  options={[
                    { value: "none", label: "None" },
                    { value: "stacked", label: "Stacked" },
                    // Percent stacking mixes units across two value axes — not offered there.
                    ...(capabilities.stacking === "full"
                      ? [{ value: "percent" as const, label: "% Stacked" }]
                      : []),
                  ]}
                  onChange={value => applyChartOverride({ stacking: value })}
                  targetWindow={targetWindow}
                  ariaLabel="Stacking"
                />
              )}

              {capabilities.legend && (
                <EditSelectRow
                  label="Legend"
                  value={chartSettings.legendPosition}
                  options={[
                    { value: "none", label: "None" },
                    { value: "top", label: "Top" },
                    { value: "bottom", label: "Bottom" },
                    { value: "left", label: "Left" },
                    { value: "right", label: "Right" },
                  ]}
                  onChange={value => applyChartOverride({ legend_position: value })}
                  targetWindow={targetWindow}
                  ariaLabel="Legend position"
                />
              )}

              {capabilities.dataLabels && (
                <EditSelectRow
                  label="Data labels"
                  value={chartSettings.dataLabels}
                  options={[
                    { value: "none", label: "None" },
                    { value: "inside", label: "Inside" },
                    { value: "outside", label: "Outside" },
                  ]}
                  onChange={value => applyChartOverride({ data_labels: value })}
                  targetWindow={targetWindow}
                  ariaLabel="Data labels"
                />
              )}

              {capabilities.axes && (
                <>
                  <EditToggleRow
                    label="X-axis"
                    checked={chartSettings.showXAxis}
                    onChange={value => applyChartOverride({ show_x_axis: value })}
                  />
                  <EditToggleRow
                    label="Y-axis"
                    checked={chartSettings.showYAxis}
                    onChange={value => applyChartOverride({ show_y_axis: value })}
                  />
                </>
              )}
            </>
          )}

          <div className="flex items-center justify-between gap-[24px] pt-[12px] pb-[12px]">
            <button
              onClick={handleEditInChat}
              className={`${BUTTON_CLASS} gap-[8px] text-card-foreground bg-card-text-button-hover-background hover:bg-card-foreground/15`}
            >
              <Sparkle2 width={16} height={16} />
              Edit in chat
            </button>
            {/* Done and dismissal both save the session, so one label fits both. */}
            <button
              onClick={handleDone}
              className={`${BUTTON_CLASS} bg-primary-button-background text-primary-button-foreground hover:bg-primary-button-hover-background hover:text-primary-button-hover-foreground`}
            >
              Done
            </button>
          </div>
        </div>
      )}
    </div>
  );
};
